import { type FormEvent, useState } from 'react';
import { ApiError } from '../api/client';
import { useAddDocumentPayment } from '../api/hooks';
import type { Document, DocumentSummary, PaymentMethod } from '../api/types';
import { Btn, Field, SelectField } from '../components/ui';
import { METHODS } from '../lib/doc';
import { czk, money, todayIso } from '../lib/format';
import { useUi } from '../state/ui';

/** Sheet „Zaznamenat úhradu / vrácení" podle openPay z prototypu. */
export function PaymentSheet({
  doc,
  onDone,
}: {
  doc: DocumentSummary;
  onDone: (updated: Document) => void;
}) {
  const isCredit = doc.type === 'DB';
  const remaining = isCredit
    ? Math.round((Number(doc.totalAmount) + Number(doc.paidAmount)) * 100) / 100
    : Number(doc.remaining);
  const [amount, setAmount] = useState(remaining.toFixed(2).replace('.', ','));
  const [date, setDate] = useState(todayIso());
  const [method, setMethod] = useState<PaymentMethod>(doc.paymentMethod);
  const [payer, setPayer] = useState(doc.customerName);
  const [note, setNote] = useState('');
  // doklad v cizí měně: platba smí mít vlastní kurz (kurz banky); rozdíl jde do kurzových rozdílů
  const foreign = !!doc.currency && doc.currency !== 'CZK' && doc.exchangeRate !== undefined;
  const [rate, setRate] = useState(doc.exchangeRate ?? '');
  const add = useAddDocumentPayment(doc.id);
  const { toast, closeSheet } = useUi();
  const err = add.error instanceof ApiError ? add.error : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const value = amount.replace(/\s/g, '').replace(',', '.');
    if (!(Number(value) > 0)) {
      toast('Zadej částku');
      return;
    }
    const rateValue = rate.replace(',', '.');
    add.mutate(
      {
        amount: value,
        date,
        method,
        payerName: payer,
        note,
        ...(foreign && rateValue && rateValue !== doc.exchangeRate
          ? { exchangeRate: rateValue }
          : {}),
      },
      {
        onSuccess: ({ document }) => {
          closeSheet();
          onDone(document);
          toast(
            document.status === 'paid'
              ? `${document.number} je zaplaceno`
              : document.status === 'settled'
                ? `${document.number} vráceno`
                : `Úhrada uložena, zbývá ${czk(document.remaining)}`,
          );
        },
      },
    );
  };

  return (
    <form onSubmit={submit}>
      <h3>{isCredit ? 'Zaznamenat vrácení' : 'Zaznamenat úhradu'}</h3>
      <div className="muted mb">
        {doc.number}, {isCredit ? 'k vrácení' : 'zbývá'}{' '}
        {foreign ? money(remaining, doc.currency) : czk(remaining)}
      </div>
      <Field
        label="Částka"
        inputMode="decimal"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        error={err?.fieldErrors.amount}
      />
      <div className="two">
        <Field label="Datum" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <SelectField
          label="Způsob"
          value={method}
          onChange={(e) => setMethod(e.target.value as PaymentMethod)}
        >
          {(Object.keys(METHODS) as PaymentMethod[]).map((k) => (
            <option key={k} value={k}>
              {METHODS[k]}
            </option>
          ))}
        </SelectField>
      </div>
      {foreign && (
        <Field
          label={`Kurz platby (CZK za 1 ${doc.currency})`}
          inputMode="decimal"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          error={err?.fieldErrors.exchangeRate}
        />
      )}
      <Field
        label={isCredit ? 'Komu vráceno' : 'Kdo zaplatil'}
        value={payer}
        onChange={(e) => setPayer(e.target.value)}
      />
      <Field
        label="Poznámka"
        placeholder="např. zaplaceno na prodejně"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {err && !Object.keys(err.fieldErrors).length && <p className="form-error">{err.message}</p>}
      <Btn variant="primary" full type="submit" disabled={add.isPending}>
        {isCredit ? 'Uložit vrácení' : 'Uložit úhradu'}
      </Btn>
      <Btn full onClick={closeSheet}>
        Zrušit
      </Btn>
    </form>
  );
}
